import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { socialAuthApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { isNative } from '../../native/platform';
import { googleConfigured, isCancelled, nativeProviders, nativeSignIn } from '../../native/auth';
import { takeSignInReturn } from '../../lib/signInReturn';

/**
 * Google + Apple sign-in buttons. Both sign in and register on the web (backend
 * /start -> provider -> backend /callback -> /auth/oauth/return handoff); Apple uses
 * the Services ID day.busybee.signin (docs/mobile/ACCOUNTS.md).
 *
 * In the iOS / Android app both sign in through the operating system
 * (src/native/auth.js) and the id_token goes to /api/auth/oauth/native; Apple is
 * offered on iOS only.
 */
const SocialLoginButtons = ({ mode = 'login' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { finalizeSession } = useAuth();
  const native = isNative();
  const showApple = !native || nativeProviders().includes('apple');
  // An app built without the Google client ids has no working Google button: none is shown.
  const showGoogle = !native || googleConfigured();

  const signInNatively = async (provider) => {
    try {
      const { idToken, fullName, authorizationCode } = await nativeSignIn(provider);
      const { data } = await socialAuthApi.native(provider, idToken, fullName, authorizationCode);
      if (data.mfa_required) {
        navigate(`/auth/oauth/return?challenge=${encodeURIComponent(data.challenge_token)}`);
        return;
      }
      await finalizeSession(data.access_token);
      navigate(takeSignInReturn(), { replace: true });
    } catch (err) {
      if (isCancelled(err)) return;
      const detail = err.response?.data?.detail;
      // The operating system's own reason (Google Credential Manager, Sign in with Apple)
      // goes under the message, so it can be reported and fixed; it also lands in the
      // device log (Logcat / Xcode console).
      const reason = !detail ? String(err?.message || err?.code || err || '').slice(0, 200) : '';
      if (reason) console.error(`[sign-in] ${provider}: ${reason}`);
      toast.error(typeof detail === 'string' ? detail : t('auth.errors.generic'),
        reason ? { description: reason } : undefined);
    }
  };

  const startProvider = (provider) => {
    if (native) {
      signInNatively(provider);
      return;
    }
    sessionStorage.setItem('oauth_intent', `${mode}:${provider}`);
    window.location.assign(socialAuthApi.startUrl(provider));
  };

  return (
    <div className="space-y-3">
      {showGoogle && (
        <button
          type="button"
          onClick={() => startProvider('google')}
          className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors"
        >
          <GoogleIcon />
          <span>{t('auth.social.continueWithGoogle')}</span>
        </button>
      )}
      {showApple && (
        <button
          type="button"
          onClick={() => startProvider('apple')}
          className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-black text-white border border-white/10 rounded-lg font-medium hover:bg-zinc-900 transition-colors"
        >
          <AppleIcon />
          <span>{t('auth.social.continueWithApple')}</span>
        </button>
      )}
    </div>
  );
};

// Inline SVG icons - keeps the bundle small and avoids loading 3rd-party brand
// asset packages. Both icons are simplified-but-recognisable monochrome
// variants permitted under Google/Apple brand guidelines for sign-in buttons.

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21.8 12.23c0-.66-.06-1.3-.17-1.91H12v3.6h5.5a4.7 4.7 0 0 1-2.04 3.09v2.56h3.3c1.93-1.78 3.04-4.4 3.04-7.34z" fill="#4285F4"/>
    <path d="M12 22c2.75 0 5.06-.91 6.74-2.46l-3.3-2.56c-.92.62-2.09.98-3.44.98-2.65 0-4.89-1.79-5.69-4.2H2.9v2.63A10 10 0 0 0 12 22z" fill="#34A853"/>
    <path d="M6.31 13.76A6 6 0 0 1 6 12c0-.61.1-1.2.27-1.76V7.61H2.9A10 10 0 0 0 2 12c0 1.61.39 3.14 1.07 4.5l3.24-2.74z" fill="#FBBC05"/>
    <path d="M12 5.84c1.5 0 2.84.52 3.9 1.53l2.93-2.93C17.05 2.94 14.74 2 12 2A10 10 0 0 0 2.9 7.61l3.4 2.63C7.11 7.63 9.35 5.84 12 5.84z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.05 12.04c-.03-2.88 2.36-4.26 2.47-4.33-1.34-1.97-3.43-2.24-4.18-2.27-1.78-.18-3.47 1.05-4.38 1.05-.91 0-2.31-1.02-3.79-1-1.95.03-3.75 1.13-4.75 2.88-2.03 3.52-.52 8.72 1.46 11.58.96 1.4 2.11 2.97 3.6 2.91 1.45-.06 2-.94 3.74-.94 1.75 0 2.25.94 3.78.91 1.56-.03 2.55-1.42 3.5-2.83 1.1-1.62 1.56-3.19 1.58-3.27-.03-.01-3.02-1.16-3.05-4.59zM14.27 4.16c.81-.97 1.35-2.32 1.2-3.66-1.16.05-2.56.77-3.39 1.74-.75.86-1.4 2.23-1.22 3.55 1.29.1 2.61-.66 3.41-1.63z"/>
  </svg>
);

export default SocialLoginButtons;
